import React, { useCallback } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import AsyncInput from '@/components/ui/async-input';
import { Button } from '@/components/ui/button';
import { CellComponentProps } from '../types';
import { predictionEngineMoviesSearch } from 'api/prediction-engine';

interface FilmSearchInputProps extends CellComponentProps {
  disabled?: boolean;
  onFilmSelect?: (rowIndex: number, filmUid: string) => void;
}

interface FilmSuggestion {
  uid: string;
  title: string;
  release_year: number;
  poster_url?: string;
}

const FilmSearchInput = React.forwardRef<
  HTMLInputElement,
  FilmSearchInputProps
>(({ value, onChange, rowIndex, column, row, disabled = false }, ref) => {
  const currentValue = value as string;
  const onFilmSelect =
    (column.cell?.props?.onFilmSelect as (
      rowIndex: number,
      filmUid: string,
    ) => void) || undefined;
  const isRowLoading = column.cell?.props?.isRowLoading as
    | ((rowIndex: number) => boolean)
    | undefined;
  const isLoading = isRowLoading ? isRowLoading(rowIndex) : false;

  const isInvalid =
    typeof column.cell?.invalid === 'function'
      ? column.cell.invalid(rowIndex)
      : column.cell?.invalid;

  const loadFilmSuggestions = useCallback(async (query: string) => {
    try {
      return await predictionEngineMoviesSearch(query);
    } catch (error) {
      console.error('Error loading film suggestions:', error);
      return [];
    }
  }, []);

  const renderFilmSuggestion = useCallback(
    (
      film: FilmSuggestion,
      index: number,
      isHighlighted: boolean,
      closeSuggestions: () => void,
    ) => {
      return (
        <button
          type="button"
          className={clsx(
            'tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors',
            'hover:tw-bg-accent hover:tw-text-accent-foreground focus:tw-bg-accent focus:tw-text-accent-foreground focus:tw-outline-none',
            isHighlighted && 'tw-bg-accent tw-text-accent-foreground',
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            onChange(film.uid);

            if (onFilmSelect) {
              onFilmSelect(rowIndex, film.uid);
            }

            setTimeout(() => {
              closeSuggestions();
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <div className="tw-flex tw-gap-3 tw-items-start tw-w-full">
            <div className="tw-flex-shrink-0 tw-w-10">
              {film.poster_url ? (
                <img
                  src={film.poster_url}
                  alt={`${film.title} poster`}
                  className="tw-w-10 tw-h-14 tw-object-cover tw-rounded tw-border tw-border-gray-200"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="tw-w-10 tw-h-14 tw-bg-gray-100 tw-rounded tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-p-1">
                  <span className="tw-text-[10px] tw-text-gray-400 tw-text-center tw-leading-tight">
                    No Image
                  </span>
                </div>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-overflow-hidden tw-text-left">
              <span className="tw-font-medium tw-text-sm tw-truncate tw-w-full tw-text-left">
                {film.title}
              </span>
              {film.release_year && (
                <span className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-text-left">
                  {film.release_year}
                </span>
              )}
            </div>
          </div>
        </button>
      );
    },
    [onChange, onFilmSelect, rowIndex],
  );

  return (
    <div className="tw-relative">
      <AsyncInput<FilmSuggestion>
        value={currentValue}
        onChange={onChange}
        placeholder="Search by title..."
        disabled={disabled}
        loadSuggestions={loadFilmSuggestions}
        renderSuggestion={renderFilmSuggestion}
        debounceMs={300}
        minChars={2}
        maxSuggestions={10}
        className={clsx(
          'tw-h-[2.5rem] !tw-text-[13px] tw-font-mono tw-pr-8 placeholder:tw-font-sans placeholder:tw-text-sm',
          isInvalid && '!tw-border-red-500 focus-visible:!tw-ring-red-500',
        )}
        error={isInvalid}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="tw-absolute tw-right-1 tw-top-1/2 -tw-translate-y-1/2 tw-h-7 tw-w-7 tw-p-0"
        disabled={isLoading || disabled || !currentValue}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onFilmSelect && onFilmSelect(rowIndex, currentValue)}
      >
        {isLoading ? (
          <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
        ) : (
          <MagnifyingGlassIcon className="tw-h-4 tw-w-4" />
        )}
      </Button>
    </div>
  );
});

FilmSearchInput.displayName = 'FilmSearchInput';

export default FilmSearchInput;
